import React from 'react';
import { Box, ButtonV2, TextV2 } from '@withjoy/joykit';
import { Warning } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import type { InternalMessage, PostFilterValues } from '../../MessagesV2.types';
import { FAIL_STATUSES } from '../../MessagesV2.constants';

const DEFAULT_FILTERS: PostFilterValues = {
  channel: 'all',
  status: 'all',
  mtype: 'all',
  hasError: false,
  recipient: '',
};

const isDefault = (filters: PostFilterValues): boolean =>
  filters.channel === 'all' && filters.status === 'all' && filters.mtype === 'all' && !filters.hasError && filters.recipient === '';

type PostFiltersProps = Readonly<{
  messages: InternalMessage[];
  filters: PostFilterValues;
  onChange: (f: PostFilterValues) => void;
}>;

export const PostFilters: React.FC<PostFiltersProps> = ({ messages, filters, onChange }) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');

  const statusOptions = ['all', ...Array.from(new Set(messages.map(m => m.rawStatus)))];
  const mtypeOptions = ['all', ...Array.from(new Set(messages.map(m => m.messageType).filter(Boolean) as string[]))];
  const errorCount = messages.filter(m => FAIL_STATUSES.has(m.rawStatus)).length;

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      gap="12px"
      padding="12px 16px"
      background="#F7F8FC"
      borderRadius={8}
      border="1px solid #E8E8E8"
    >
      {/* Channel */}
      <Box
        display="flex"
        alignItems="center"
        gap="6px"
      >
        <TextV2
          typographyVariant="label3"
          color="mono10"
        >
          {tq`Channel`}
        </TextV2>
        <select
          value={filters.channel}
          onChange={e => onChange({ ...filters, channel: e.target.value as PostFilterValues['channel'] })}
          style={{ fontSize: '13px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #D8D8D8' }}
        >
          <option value="all">{tq`All`}</option>
          <option value="email">{tq`Email`}</option>
          <option value="sms">{tq`SMS`}</option>
        </select>
      </Box>

      {/* Status */}
      <Box
        display="flex"
        alignItems="center"
        gap="6px"
      >
        <TextV2
          typographyVariant="label3"
          color="mono10"
        >
          {tq`Status`}
        </TextV2>
        <select
          value={filters.status}
          onChange={e => onChange({ ...filters, status: e.target.value })}
          style={{ fontSize: '13px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #D8D8D8' }}
        >
          {statusOptions.map(s => (
            <option
              key={s}
              value={s}
            >
              {s === 'all' ? tq`All` : s}
            </option>
          ))}
        </select>
      </Box>

      {/* Message type */}
      <Box
        display="flex"
        alignItems="center"
        gap="6px"
      >
        <TextV2
          typographyVariant="label3"
          color="mono10"
        >
          {tq`Type`}
        </TextV2>
        <select
          value={filters.mtype}
          onChange={e => onChange({ ...filters, mtype: e.target.value })}
          style={{ fontSize: '13px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #D8D8D8' }}
        >
          {mtypeOptions.map(t => (
            <option
              key={t}
              value={t}
            >
              {t === 'all' ? tq`All` : t.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </Box>

      {/* Has error toggle — styled as a prominent pill so CX can't miss it */}
      <Box
        as="button"
        type="button"
        display="inline-flex"
        alignItems="center"
        gap="6px"
        paddingX="12px"
        paddingY="6px"
        borderRadius="100px"
        border="2px solid"
        cursor="pointer"
        fontWeight={600}
        fontSize="13px"
        style={
          filters.hasError
            ? { background: 'rgb(153,0,25)', borderColor: 'rgb(153,0,25)', color: '#fff' }
            : { background: 'rgb(255,229,234)', borderColor: 'rgb(220,38,38)', color: 'rgb(153,0,25)' }
        }
        onClick={() => onChange({ ...filters, hasError: !filters.hasError })}
      >
        <Warning size="sm" />
        {tq`Errors only`}
        {errorCount > 0 && (
          <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            paddingX="6px"
            paddingY="1px"
            borderRadius="100px"
            fontSize="11px"
            fontWeight={700}
            style={filters.hasError ? { background: 'rgba(255,255,255,0.25)', color: '#fff' } : { background: 'rgb(220,38,38)', color: '#fff' }}
          >
            {errorCount}
          </Box>
        )}
      </Box>

      {/* Recipient text filter */}
      <Box
        display="flex"
        alignItems="center"
        gap="6px"
      >
        <TextV2
          typographyVariant="label3"
          color="mono10"
        >
          {tq`Recipient`}
        </TextV2>
        <input
          value={filters.recipient}
          onChange={e => onChange({ ...filters, recipient: e.target.value })}
          placeholder={tq`filter...`}
          style={{ fontSize: '13px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #D8D8D8', width: '160px' }}
        />
      </Box>

      {/* Clear */}
      {!isDefault(filters) && (
        <ButtonV2
          size="sm"
          variant="ghost"
          onClick={() => onChange(DEFAULT_FILTERS)}
        >
          {tq`Clear filters`}
        </ButtonV2>
      )}
    </Box>
  );
};

export { DEFAULT_FILTERS };
